import { Directive, ElementRef, HostListener, Renderer2, OnInit, OnDestroy } from "@angular/core";

@Directive({
  selector: "[dragToScroll]",
  standalone: false,
})
export class DragToScrollDirective implements OnInit, OnDestroy {
  private isDown = false;
  private startX: number;
  private scrollLeft: number;
  private hasMoved = false;
  private justDragged = false;
  private clickListener: () => void;

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2
  ) {
    // Add cursor style to indicate draggability
    this.renderer.setStyle(this.elementRef.nativeElement, "cursor", "grab");
    this.renderer.setStyle(this.elementRef.nativeElement, "user-select", "none");
  }

  ngOnInit() {
    // Add capture phase click listener to catch clicks before they reach children
    const clickHandler = (event: MouseEvent) => {
      if (this.justDragged) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        this.justDragged = false;
      }
    };

    this.elementRef.nativeElement.addEventListener("click", clickHandler, true);

    // Store cleanup function
    this.clickListener = () => {
      this.elementRef.nativeElement.removeEventListener("click", clickHandler, true);
    };
  }

  ngOnDestroy() {
    if (this.clickListener) {
      this.clickListener();
    }
  }

  @HostListener("mousedown", ["$event"])
  onMouseDown(event: MouseEvent): void {
    // Don't start dragging if clicking on interactive elements
    const target = event.target as HTMLElement;
    if (target.tagName === "BUTTON" || target.tagName === "A" || target.closest("button") || target.closest("a")) {
      return;
    }

    this.isDown = true;
    this.hasMoved = false;
    this.elementRef.nativeElement.style.cursor = "grab";
    const rect = this.elementRef.nativeElement.getBoundingClientRect();
    this.startX = event.pageX - rect.left;
    this.scrollLeft = this.elementRef.nativeElement.scrollLeft;
  }

  @HostListener("mouseleave")
  onMouseLeave(): void {
    this.isDown = false;
    this.elementRef.nativeElement.style.cursor = "grab";
  }

  @HostListener("mouseup", ["$event"])
  onMouseUp(event: MouseEvent): void {
    if (this.isDown && this.hasMoved) {
      // Mark that we just dragged to prevent click events
      this.justDragged = true;
      // Prevent click events if we were dragging
      event.preventDefault();
      // Clear the flag after a short delay to allow normal clicks again
      setTimeout(() => {
        this.justDragged = false;
      }, 100);
    }
    this.isDown = false;
    this.hasMoved = false;
    this.elementRef.nativeElement.style.cursor = "grab";
  }

  @HostListener("mousemove", ["$event"])
  onMouseMove(event: MouseEvent): void {
    if (!this.isDown) return;

    const rect = this.elementRef.nativeElement.getBoundingClientRect();
    const x = event.pageX - rect.left;
    const walk = x - this.startX; // 1:1 scroll speed with mouse movement

    // Only start dragging if mouse has moved a bit (prevents accidental drag on click)
    if (Math.abs(walk) > 3) {
      this.hasMoved = true;
      this.elementRef.nativeElement.style.cursor = "grabbing";
      event.preventDefault();
      this.elementRef.nativeElement.scrollLeft = this.scrollLeft - walk;
    }
  }
}
